import { useMemo, useState } from 'react';

import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import AssignAssessmentActivitiesModal from 'approot/shared/assign/assign-assessment-activities-modal';
import AssignLessonActivitiesModal from 'approot/shared/assign/assign-lesson-activities-modal';
import DifferentiationShape from 'approot/shared/differentiation/differentiation-shape';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { Box } from 'approot/shared/design-system/box/box.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { ASSESSMENT_ACTIVITIES, LESSON_ACTIVITIES } from 'global/constants';
import { Props } from './activity-print.constants';
import ActivityViewDetails from './activity-view-details';
import * as Styled from './activity-view.style';
import { getActivityDetails, getActivityNumber } from './activity.utils';
import { useUpdateActivityBookmarkData } from 'approot/activity-builder/logic/update-activity-bookmark-data.hooks';
import BookmarkButton from 'approot/shared/bookmarks/bookmark-button/bookmark';
import SimpleModal from 'lib/modal/simple-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';

const ActivityView = ({
  section,
  sectionsOverview,
  hideCheckpoint,
  canSeeExtraDetails = true,
  unitId,
  isAssigned,
  canAssign = true,
}: Props & {
  canSeeExtraDetails?: boolean;
  unitId?: number;
  isAssigned?: boolean;
  canAssign?: boolean;
}) => {
  const [isAssignModalVisible, setIsAssignModalVisible] =
    useState<boolean>(false);

  const {
    isVisible: isMenuVisible,
    openModal: openMenuModal,
    closeModal: closeMenuModal,
  } = useSimpleModalState();

  const { contentId, contentType, version, lang } =
    useGetInteractiveLessonParams();

  const { registerHeading } = useAccessibleHeading();
  const { updateBookmarkData } = useUpdateActivityBookmarkData({
    id: contentId,
    language: lang,
    version: version || '',
    contentType,
  });

  const {
    differentiation,
    progressLabel,
    totalSteps,
    currentStep,
    parentSectionId,
    isCheckPoint,
  } = useMemo(() => {
    return getActivityDetails({
      section,
      sectionsOverview,
    });
  }, [section, sectionsOverview]);

  const number = useMemo(() => {
    return getActivityNumber(parentSectionId || '', sectionsOverview);
  }, [parentSectionId, sectionsOverview]);

  const isAssignModalAvailable = unitId && canAssign && version;
  const isDifferentiationBlueSquare =
    differentiation === Differentiation.BlueSquare;

  const differentiationIcons = (
    <>
      {differentiation && (
        <DifferentiationShape
          differentiation={differentiation}
          number={number}
          width={48}
          height={48}
        />
      )}
      {isCheckPoint && !hideCheckpoint && (
        <Styled.ActivityViewCheckpoint
          id="icon_star"
          $isActive
          height={28}
          width={28}
        />
      )}
    </>
  );

  return (
    <Styled.ActivityViewContainer>
      <ScreenReaderOnly
        as="h1"
        key={section.id}
        ref={registerHeading}
        tabIndex={-1}
      >
        {`Activity ${number}${totalSteps > 1 ? ` (${progressLabel})` : ''}`}
      </ScreenReaderOnly>

      {canSeeExtraDetails ? (
        <Styled.ActivityViewButtonsWrapper
          aria-label={`Activity ${number}${
            totalSteps > 1 ? ` (${progressLabel})` : ''
          }`}
          $isRound={!isDifferentiationBlueSquare}
          $isCircle={!isAssignModalAvailable && !isDifferentiationBlueSquare}
        >
          <>
            <Styled.ActivityViewDetailsButton
              type="button"
              aria-label="Click to see differentiation details"
              aria-haspopup
              $isRound={!isDifferentiationBlueSquare}
              onClick={openMenuModal}
            >
              {differentiationIcons}
            </Styled.ActivityViewDetailsButton>

            {totalSteps > 1 && (
              <Styled.ActivityViewText>{progressLabel}</Styled.ActivityViewText>
            )}

            {isAssignModalAvailable && (
              <Box $marginRight={8} $marginLeft={totalSteps > 1 ? 8 : 16}>
                <BookmarkButton
                  bookmarked={!!isAssigned}
                  contentType={contentType}
                  contentId={contentId}
                  assignButtonLocation="activity-page"
                  onAssignSelect={() => setIsAssignModalVisible(true)}
                />
              </Box>
            )}
          </>
        </Styled.ActivityViewButtonsWrapper>
      ) : (
        <Styled.ActivityViewDifferentiation
          aria-hidden
          $isRound={!isDifferentiationBlueSquare}
        >
          {differentiationIcons}
          {totalSteps > 1 && (
            <Styled.ActivityViewText>{progressLabel}</Styled.ActivityViewText>
          )}
        </Styled.ActivityViewDifferentiation>
      )}

      {isMenuVisible && (
        <SimpleModal
          title="Activity Description"
          isVisible
          size={380}
          onClose={closeMenuModal}
        >
          <ActivityViewDetails
            differentiation={differentiation}
            isCheckPoint={isCheckPoint}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
          />
        </SimpleModal>
      )}

      {isAssignModalVisible && contentType === LESSON_ACTIVITIES && (
        <AssignLessonActivitiesModal
          version={version || ''}
          onClose={() => setIsAssignModalVisible(false)}
          contentId={contentId}
          unitId={unitId!}
          onCompleted={updateBookmarkData}
        />
      )}

      {isAssignModalVisible && contentType === ASSESSMENT_ACTIVITIES && (
        <AssignAssessmentActivitiesModal
          version={version || ''}
          onClose={() => setIsAssignModalVisible(false)}
          contentId={contentId}
          unitId={unitId!}
          onCompleted={updateBookmarkData}
        />
      )}
    </Styled.ActivityViewContainer>
  );
};

export default ActivityView;
